import {
  FETCHING_PALETTE_IDS_REQUEST,
  FETCHING_PALETTE_IDS_SUCCEEDED,
  FETCHING_PALETTE_IDS_FAILED,
  UPDATE_BRANDKIT_ENABLE_TOGGLE_REQUEST,
  UPDATING_BRANDKIT_ENABLE_TOGGLE_SUCCEEDED,
  UPDATING_BRANDKIT_ENABLE_TOGGLE_FAILED,
  CREATE_EDITOR_BRAND_MEDIA_REQUEST,
  CREATING_EDITOR_BRAND_MEDIA_SUCCEEDED,
  CREATING_EDITOR_BRAND_MEDIA_FAILED,
  CREATE_EDITOR_BRAND_COMPONENT_REQUEST,
  CREATING_EDITOR_BRAND_COMPONENT_SUCCEEDED,
  CREATING_EDITOR_BRAND_COMPONENT_FAILED,
  INVALIDATE_BRAND_ELEMENTS,
} from "constants/actionTypes";
import { call, put, select, takeEvery } from "redux-saga/effects";
import axios from "axios";
import { createAction } from "redux-actions";
import buildUrl from "build-url";
import _ from "lodash";
import { PALETTE_COMPONENT_WIDGET } from "constants/brandKit/component";
import {
  ANCHOR,
  IMAGE,
  LIVE_ELEMENT_CONTAINER,
  VIDEO,
  PAGE,
  MODAL,
  SLIDE,
  FORM_FIELD,
  FORM_NUMBER,
  FORM_TEXTAREA,
  FORM_DROPDOWN_LIST,
  FORM_CHECK_LIST,
  FORM_CHECK_BOX,
  TEXT,
  HOTSPOT,
  MENU_CONTAINER,
  LIVE_ELEMENT,
  MENU_DRAGGABLE_CONTAINER,
} from "constants/widgetTypes";
import { Frame } from "scenejs";
import shortid from "shortid";
import { getBoundingRectFromLayout } from "components/Editor/helper/layoutManager";
import { broadcastMessage } from "components/Broadcaster";
import { reloadEditorPaletteComponentSucceed } from "actions/palette";

const apiUrl = `${process.env.REACT_APP_CLEVERSTORY_API_URL}/api/2.0`;
const BRANDKIT_COMPONENTS_URL = `${apiUrl}/palette/component`;

/**
 * Fetch PaletteIds
 */
export const fetchPaletteIds = createAction(FETCHING_PALETTE_IDS_REQUEST);

const fetchingPaletteIdsSucceeded = createAction(
  FETCHING_PALETTE_IDS_SUCCEEDED,
  (response) => {
    return {
      response: response,
    };
  }
);

const fetchingPaletteIdsFailed = createAction(
  FETCHING_PALETTE_IDS_FAILED,
  (error) => {
    return {
      error: error,
    };
  }
);

function* fetchPaletteIdsSaga() {
  let url = buildUrl(apiUrl, {
    path: `/palette`,
  });

  const fetchPaletteIdsAPI = () => {
    return axios.get(url).then((response) => response.data);
  };

  try {
    const response = yield call(fetchPaletteIdsAPI);
    if (response) {
      yield put(fetchingPaletteIdsSucceeded(response));
    }
  } catch (error) {
    yield put(fetchingPaletteIdsFailed(error));
  }
}

/**
 * Update Brankit Enable option
 */
export const updateBrandkitEnableToggle = createAction(
  UPDATE_BRANDKIT_ENABLE_TOGGLE_REQUEST,
  (id, request) => {
    return {
      id: id,
      request: request,
    };
  }
);

export const updateBrandkitEnableToggleSucceeded = createAction(
  UPDATING_BRANDKIT_ENABLE_TOGGLE_SUCCEEDED,
  (response) => {
    return {
      response: response,
    };
  }
);

export const updateBrandkitEnableToggleFailed = createAction(
  UPDATING_BRANDKIT_ENABLE_TOGGLE_FAILED,
  (id, error) => {
    return {
      id: id,
      error: error,
    };
  }
);

function* updateBrandkitEnableToggleSaga(action) {
  const { id, request } = action.payload;

  let url = buildUrl(apiUrl, {
    path: `/palette/${id}`,
  });

  const updateBrandkitEnableToggleAPI = () => {
    return axios.put(url, request).then((response) => response.data);
  };

  try {
    const response = yield call(updateBrandkitEnableToggleAPI);
    if (response) {
      yield put(updateBrandkitEnableToggleSucceeded(response));
    }
  } catch (error) {
    yield put(updateBrandkitEnableToggleFailed(error));
  }
}

/** ------- Editor Element BrandKit Saga -------  */
// Media actions
export const createEditorBrandMedia = createAction(
  CREATE_EDITOR_BRAND_MEDIA_REQUEST,
  (uid, request, brandkitId, type) => ({ uid, request, brandkitId, type })
);

export const creatingEditorBrandSucceeded = createAction(
  CREATING_EDITOR_BRAND_MEDIA_SUCCEEDED,
  ({ uid, brandkitId, response, type }) => ({ uid, brandkitId, response, type })
);

export const creatingEditorBrandFailed = createAction(
  CREATING_EDITOR_BRAND_MEDIA_FAILED,
  (uid, error) => ({ uid, error })
);

export const invalidateEditorBrand = createAction(
  INVALIDATE_BRAND_ELEMENTS,
  (uid) => ({ uid })
);

function* createEditorBrandMediaSaga(action) {
  const { request, uid, brandkitId, type } = action.payload;

  let url = buildUrl(apiUrl, {
    path: `/palette/${brandkitId}/media/${request.id}`,
  });

  const createBrandMediaAPI = async () => {
    return axios.post(url, {}).then((response) => response.data);
  };

  const createBrandButtonAPI = async () => {
    url = buildUrl(`${apiUrl}/palette`, {
      path: `/${brandkitId}`,
    });
    return axios
      .put(url, {
        components: request,
      })
      .then((response) => response.data);
  };

  try {
    if (type === "BUTTON") {
      const response = yield call(createBrandButtonAPI);
      if (response) {
        yield put(
          creatingEditorBrandSucceeded({
            uid,
            brandkitId,
            response,
            type,
          })
        );
        yield put(
          reloadEditorPaletteComponentSucceed(brandkitId, response, type)
        );
        yield broadcastMessage(
          creatingEditorBrandComponentSucceeded(uid, brandkitId, response, type)
        );
      }
      return;
    }

    const response = yield call(createBrandMediaAPI);
    if (response) {
      yield put(
        creatingEditorBrandSucceeded({
          uid,
          brandkitId,
          response,
          type,
        })
      );
      yield put(
        reloadEditorPaletteComponentSucceed(brandkitId, response, type)
      );
      yield broadcastMessage(
        creatingEditorBrandComponentSucceeded(uid, brandkitId, response, type)
      );
    }
  } catch (error) {
    yield put(creatingEditorBrandFailed(uid, true));
  }
}

// Component actions
export const createEditorBrandComponent = createAction(
  CREATE_EDITOR_BRAND_COMPONENT_REQUEST,
  ({ uid, brandkitId, selectedWidgets, editorSession, name, themes }) => ({
    uid,
    brandkitId,
    selectedWidgets,
    editorSession,
    name,
    themes,
  })
);

export const creatingEditorBrandComponentSucceeded = createAction(
  CREATING_EDITOR_BRAND_COMPONENT_SUCCEEDED,
  (uid, brandkitId, response, type) => ({ uid, brandkitId, response, type })
);

export const creatingEditorBrandComponentFailed = createAction(
  CREATING_EDITOR_BRAND_COMPONENT_FAILED,
  (uid, error) => ({ uid, error })
);

function* createEditorBrandComponentSaga(action) {
  const { name, themes, uid, brandkitId, selectedWidgets, editorSession } =
    action.payload;
  const request = { name, themes };

  try {
    const allWidgets = yield select(
      (state) => _.get(state, `${editorSession}.present.widgets`) || []
    );
    const clonedWidgets = _.cloneDeep(allWidgets);

    const extractSelectedSubtrees = () => {
      const uidToWidget = new Map(clonedWidgets.map((w) => [w.uid, w]));
      const uidToChildren = new Map();

      clonedWidgets.forEach((widget) => {
        if (!uidToChildren.has(widget.parent)) {
          uidToChildren.set(widget.parent, []);
        }
        uidToChildren.get(widget.parent).push(widget);
      });

      const collected = [];

      const collect = (uid, parentUid = null) => {
        let widget = uidToWidget.get(uid);
        if (
          !widget ||
          [ANCHOR, HOTSPOT].includes(widget.type)
        )
          return;

        const checkAction = (widget.actions || []).filter(
          (a) =>
            !["asset", "page", "widgetAnchor", "gating", "slide", "carousel"].includes(
              a.type
            )
        );

        let cleanedWidget = {
          ..._.omit(widget, ["id", "editorSession", "variableMappings"]),
          parent: parentUid,
          actions: checkAction,
          template: null,
          systemDefined: false,
          properties: {
            ...widget.properties,
            desktop: {
              ...widget.properties.desktop,
              variables: [],
            },
            mobile: {
              ...widget.properties.mobile,
              variables: [],
            },
          },
        };

        if(cleanedWidget.type === MENU_CONTAINER || cleanedWidget.type === MENU_DRAGGABLE_CONTAINER) {
          cleanedWidget.content = cleanedWidget.content.map((item) => {
            if(item.children && item.children.length > 0) {
              item.children = item.children.map((child) => {
                return {
                  ...child,
                  action: !["asset", "page", "widgetAnchor", "gating", "slide", "carousel"].includes(child.action?.type) ? child.action : null,
                };
              });
            }

            return {
              ...item,
              action: !["asset", "page", "widgetAnchor", "gating", "slide", "carousel"].includes(item.action?.type) ? item.action : null,
            };
          });
        }

        if(cleanedWidget.type === LIVE_ELEMENT) {
          delete cleanedWidget.children;
        }

        if (
          [
            VIDEO,
            IMAGE,
            FORM_FIELD,
            FORM_NUMBER,
            FORM_TEXTAREA,
            FORM_DROPDOWN_LIST,
            FORM_CHECK_LIST,
            FORM_CHECK_BOX,
            LIVE_ELEMENT
          ].includes(cleanedWidget.type)
        ) {
          cleanedWidget.content = cleanedWidget.content?.id || cleanedWidget.content || "";
        }

        if (
          cleanedWidget.content &&
          typeof cleanedWidget.content === "string" &&
          cleanedWidget.type === TEXT &&
          checkAction
        ) {
          let updatedContent = cleanedWidget.content;

          updatedContent = updatedContent.replace(/<a[^>]*>(.*?)<\/a>/g, "$1");

          updatedContent = updatedContent.replace(
            /<span[^>]*\sdata-variable-id=["'][^"']*["'][^>]*>(.*?)<\/span>/g,
            "$1"
          );

          cleanedWidget.content = updatedContent;
        }

        if (selectedWidgets.map((w) => w.uid).includes(widget.uid)) {
          let desktopLayout = reCalculateWidgetLayout(
            cleanedWidget,
            _.get(cleanedWidget, `properties.desktop.layout`),
            "desktop"
          );
          let mobileLayout = reCalculateWidgetLayout(
            cleanedWidget,
            _.get(cleanedWidget, `properties.mobile.layout`),
            "mobile"
          );

          cleanedWidget = {
            ...cleanedWidget,
            properties: {
              ...cleanedWidget.properties,
              desktop: {
                ...cleanedWidget.properties.desktop,
                layout: desktopLayout,
              },
              mobile: {
                ...cleanedWidget.properties.mobile,
                layout: mobileLayout,
              },
            },
          };
        }

        if (cleanedWidget.properties.desktop?.background) {
          cleanedWidget.properties.desktop.background.media =
            cleanedWidget.properties.desktop.background.media?.id ||
            cleanedWidget.properties.desktop.background.media ||
            "";
        }
        if (cleanedWidget.properties.mobile?.background) {
          cleanedWidget.properties.mobile.background.media =
            cleanedWidget.properties.mobile.background.media?.id ||
            cleanedWidget.properties.mobile.background.media ||
            "";
        }
        collected.push(cleanedWidget);

        const children = uidToChildren.get(uid) || [];
        children.forEach((child) => {
          collect(child.uid, uid);
        });
      };

      selectedWidgets.forEach((widget) => {
        collect(widget.uid, widget.parent);
      });

      return collected;
    };

    const remapWidgetUids = (widgets) => {
      const uidMap = new Map();

      // Step 1: Generate new UIDs
      widgets.forEach((widget) => {
        uidMap.set(widget.uid, `widget_${shortid.generate()}`);
      });

      // Step 2: Create created widgets with new uids and parents
      return widgets.map((widget) => {
        const selectedWidget = selectedWidgets.find(
          (w) => w.uid === widget.uid
        );

        return {
          ...widget,
          uid: uidMap.get(widget.uid),
          parent: selectedWidget
            ? "widget_SfVs0utv"
            : widget.parent
            ? uidMap.get(widget.parent)
            : null,
        };
      });
    };

    const createAndSaveBrandComponent = async ({ request, widgets }) => {
      const updateUrl = buildUrl(BRANDKIT_COMPONENTS_URL, {
        path: `/${brandkitId}/create`,
      });

      const payload = {
        name: request.name,
        themes: request?.themes || [],
        widgets: [PALETTE_COMPONENT_WIDGET, ...widgets],
      };

      const response = await axios.post(updateUrl, payload);
      const data = response.data;
      if (![200, 201].includes(response.status)) {
        throw new Error("Failed to save brand component");
      }

      const publishUrl = buildUrl(BRANDKIT_COMPONENTS_URL, {
        path: `/${data.id}/publish`,
      });

      await axios.get(publishUrl);
      return data;
    };

    const _findWidget = (id) => {
      return allWidgets.find((widget) => widget.uid === id);
    };

    const reCalculateWidgetLayout = (widget, layout, breakpoint) => {
      var wRect = getBoundingRectFromLayout(layout);

      var parent = _findWidget(widget.parent);
      var pLayout = _.get(parent, `properties.${breakpoint}.layout`);
      var pRect = getBoundingRectFromLayout(pLayout);

      const frame = new Frame(layout);
      var x = wRect.x + pRect.x;
      var y = wRect.y + pRect.y;

      const translate = `${x}px, ${y}px`;

      frame.set("transform", "translate", translate);
      var updatedLayout = frame.get();

      if (!_.includes([PAGE, MODAL, SLIDE, LIVE_ELEMENT_CONTAINER], parent.type)) {
        return reCalculateWidgetLayout(parent, updatedLayout, breakpoint);
      }

      return updatedLayout;
    };

    const subtreeWidgets = extractSelectedSubtrees(clonedWidgets);
    const remappedWidgets = remapWidgetUids(subtreeWidgets);

    const response = yield call(createAndSaveBrandComponent, {
      request,
      widgets: remappedWidgets,
    });

    yield put(creatingEditorBrandComponentSucceeded(uid, brandkitId, response));

    if (response && response.id) {
      yield put(invalidateEditorBrand(uid));
      yield put(reloadEditorPaletteComponentSucceed(brandkitId, response));
      yield broadcastMessage(
        creatingEditorBrandComponentSucceeded(uid, brandkitId, response)
      );
    }
  } catch (error) {
    console.log("Error creating editor brand component:", error);
    yield put(creatingEditorBrandComponentFailed(uid, true));
  }
}

export default function* brandKitSaga() {
  yield takeEvery(FETCHING_PALETTE_IDS_REQUEST, fetchPaletteIdsSaga);
  yield takeEvery(
    UPDATE_BRANDKIT_ENABLE_TOGGLE_REQUEST,
    updateBrandkitEnableToggleSaga
  );
  yield takeEvery(
    CREATE_EDITOR_BRAND_MEDIA_REQUEST,
    createEditorBrandMediaSaga
  );
  yield takeEvery(
    CREATE_EDITOR_BRAND_COMPONENT_REQUEST,
    createEditorBrandComponentSaga
  );
}
